import dagre from '@dagrejs/dagre';

/**
 * Auto-layout Dagre du canevas ECA (axe du flux = gauche → droite).
 *
 * Dagre ne connaît que des noeuds et des arêtes : il ignore totalement les
 * poignées (handles) et leur position verticale sur le noeud. Or plusieurs
 * noeuds ECA ont des poignées ORDONNÉES :
 *   - condition : TRUE à 35 % (haut) et FALSE à 65 % (bas) ;
 *   - gateway   : une entrée par branche, de haut en bas (`flux-in-branch-N`).
 * Sans contrainte, Dagre place volontiers la cible du FALSE au-dessus de celle
 * du TRUE : le fil vert descend, le fil rouge remonte, et les deux se croisent
 * au sortir de la condition.
 *
 * Parade en deux temps :
 *   1. `constraints` — Dagre accepte des paires `{ left, right }` (« left avant
 *      right dans la rangée ») et les respecte pendant sa minimisation de
 *      croisements. Efficace tant que les deux cibles tombent dans la MÊME
 *      rangée.
 *   2. `customOrder` — une branche qui rejoint le tronc plus loin (« skip »)
 *      traverse plusieurs rangées sous forme de noeuds fantômes, que Dagre seul
 *      sait nommer. On rejoue donc son ordonnancement par défaut, puis on
 *      réordonne chaque rangée pour que le chemin du TRUE reste au-dessus de
 *      celui du FALSE sur TOUTE sa longueur, fantômes compris.
 */

/** Ordre vertical des poignées de SORTIE d'une condition (0 = la plus haute). */
const SOURCE_HANDLE_ORDER = { 'flux-out-true': 0, 'flux-out-false': 1 };

/** Index de branche d'une poignée d'ENTRÉE de gateway, ou null. */
const branchIndex = (handle) => {
  const m = /^flux-in-branch-(\d+)$/.exec(handle ?? '');
  return m ? Number(m[1]) : null;
};

/** Dimensions : la mesure réelle de ReactFlow, sinon une estimation sûre. */
const measured = (node) => ({
  w: node.measured?.width  ?? (node.type === 'variable' ? 120 : 230),
  h: node.measured?.height ?? (node.type === 'variable' ? 28  : 110),
});

// ---------------------------------------------------------------------------
// Paires d'arêtes à ordonner
// ---------------------------------------------------------------------------

/**
 * Paires d'arêtes de flux consécutives sur une même poignée ordonnée :
 * `[arêteDuHaut, arêteDuBas]`. Une paire dont les deux arêtes mènent au même
 * noeud n'a rien à ordonner et n'est pas produite.
 *
 * @param {Array} fluxEdges Arêtes de flux.
 * @returns {Array<[object, object]>}
 */
export const orderedEdgePairs = (fluxEdges) => {
  const pairs = [];

  const chainGroup = (groups, rank) => {
    for (const list of groups.values()) {
      list.sort((a, b) => rank(a) - rank(b));
      for (let i = 0; i < list.length - 1; i++) {
        const [up, down] = [list[i], list[i + 1]];
        if (up.source !== down.source || up.target !== down.target) pairs.push([up, down]);
      }
    }
  };

  // Sorties ordonnées d'une condition : la branche TRUE au-dessus de la FALSE.
  const outgoing = new Map();
  for (const e of fluxEdges) {
    if (!(e.sourceHandle in SOURCE_HANDLE_ORDER)) continue;
    if (!outgoing.has(e.source)) outgoing.set(e.source, []);
    outgoing.get(e.source).push(e);
  }
  chainGroup(outgoing, (e) => SOURCE_HANDLE_ORDER[e.sourceHandle]);

  // Entrées ordonnées d'une gateway : la branche 0 au-dessus de la branche 1…
  const incoming = new Map();
  for (const e of fluxEdges) {
    if (branchIndex(e.targetHandle) === null) continue;
    if (!incoming.has(e.target)) incoming.set(e.target, []);
    incoming.get(e.target).push(e);
  }
  chainGroup(incoming, (e) => branchIndex(e.targetHandle));

  return pairs;
};

/**
 * Contraintes `{ left, right }` passées à Dagre : premier filet, appliqué
 * pendant la minimisation des croisements. Les paires contradictoires (deux
 * conditions qui se croisent les branches) sont abandonnées — Dagre boucle sur
 * un graphe de contraintes cyclique.
 *
 * @param {Array} nodes     Noeuds ReactFlow.
 * @param {Array} fluxEdges Arêtes de flux.
 * @returns {Array<{left: string, right: string}>}
 */
export const handleOrderConstraints = (nodes, fluxEdges) => {
  const knownIds = new Set(nodes.map((n) => n.id));
  const constraints = [];
  const after = new Map();   // noeud → fermeture transitive de ce qui doit venir après

  for (const [up, down] of orderedEdgePairs(fluxEdges)) {
    const [left, right] = [up.target, down.target];
    if (left === right || !knownIds.has(left) || !knownIds.has(right)) continue;
    const leftAfter  = after.get(left)  ?? new Set();
    const rightAfter = after.get(right) ?? new Set();
    if (leftAfter.has(right)) continue;   // déjà imposé
    if (rightAfter.has(left)) continue;   // cycle → on laisse Dagre décider
    constraints.push({ left, right });
    const closure = new Set([right, ...rightAfter]);
    after.set(left, new Set([...leftAfter, ...closure]));
    for (const [node, set] of after) {
      if (node !== left && set.has(left)) closure.forEach((c) => set.add(c));
    }
  }

  return constraints;
};

// ---------------------------------------------------------------------------
// Réordonnancement des rangées (graphe interne de Dagre, fantômes compris)
// ---------------------------------------------------------------------------

const isSameEdge = (edgeObj, v, w) =>
  !!edgeObj && ((edgeObj.v === v && edgeObj.w === w) || (edgeObj.v === w && edgeObj.w === v));

/**
 * Premier (ou dernier) noeud du chemin que Dagre a donné à une arête : la cible
 * elle-même si elle est dans la rangée voisine, sinon le noeud fantôme qui
 * ouvre la file. `null` si l'arête a disparu (auto-arête, noeud supprimé).
 */
const firstHop = (g, from, to) => {
  const successors = g.successors(from) ?? [];
  return successors.includes(to)
    ? to
    : (successors.find((v) => isSameEdge(g.node(v)?.edgeObj, from, to)) ?? null);
};

const lastHop = (g, from, to) => {
  const predecessors = g.predecessors(to) ?? [];
  return predecessors.includes(from)
    ? from
    : (predecessors.find((v) => isSameEdge(g.node(v)?.edgeObj, from, to)) ?? null);
};

/** Cône aval (ou amont) d'un noeud, noeuds fantômes compris, lui inclus. */
const cone = (g, start, forward) => {
  const seen = new Set([start]);
  const stack = [start];
  while (stack.length) {
    const v = stack.pop();
    for (const n of (forward ? g.successors(v) : g.predecessors(v)) ?? []) {
      if (!seen.has(n)) { seen.add(n); stack.push(n); }
    }
  }
  return seen;
};

/**
 * Tri topologique STABLE d'une rangée : l'ordre issu de Dagre est conservé
 * partout où les précédences ne disent rien. Les précédences qui forment un
 * cycle sont abandonnées plutôt que de renvoyer une rangée tronquée.
 */
const stableTopoSort = (layer, precedences) => {
  const rank = new Map(layer.map((v, i) => [v, i]));
  const outgoing = new Map(layer.map((v) => [v, []]));
  const indegree = new Map(layer.map((v) => [v, 0]));
  for (const [a, b] of precedences) {
    if (!rank.has(a) || !rank.has(b) || a === b) continue;
    outgoing.get(a).push(b);
    indegree.set(b, indegree.get(b) + 1);
  }

  const sorted = [];
  const pending = new Set(layer);
  while (pending.size) {
    let next = layer.find((v) => pending.has(v) && indegree.get(v) === 0);
    if (next === undefined) next = layer.find((v) => pending.has(v)); // cycle : on relâche
    pending.delete(next);
    sorted.push(next);
    for (const w of outgoing.get(next)) indegree.set(w, indegree.get(w) - 1);
  }
  return sorted;
};

/**
 * Impose, rangée par rangée, que la branche « du haut » passe au-dessus de la
 * branche « du bas » sur TOUTE sa longueur (noeuds fantômes des arêtes longues
 * compris), et pas seulement au premier saut.
 *
 * Le tronc commun aux deux branches (le point de jonction et sa suite) est hors
 * contrainte : il n'appartient à aucune des deux.
 *
 * @param {object} g     Graphe interne de Dagre (rangs et ordres déjà calculés).
 * @param {Array}  pairs Paires `[arêteDuHaut, arêteDuBas]`.
 */
const enforceHandleOrder = (g, pairs) => {
  const precedencesByRank = new Map();

  for (const [up, down] of pairs) {
    // Branches divergentes (sorties d'une condition) : on regarde vers l'aval.
    // Branches convergentes (entrées d'une gateway) : vers l'amont.
    const forward = up.source === down.source;
    const hop = forward ? firstHop : lastHop;
    const upHop   = hop(g, up.source, up.target);
    const downHop = hop(g, down.source, down.target);
    if (!upHop || !downHop || upHop === downHop) continue;

    const upCone   = cone(g, upHop, forward);
    const downCone = cone(g, downHop, forward);
    const upOnly   = [...upCone].filter((v) => !downCone.has(v));
    const downOnly = [...downCone].filter((v) => !upCone.has(v));

    const upByRank = new Map();
    for (const v of upOnly) {
      const { rank } = g.node(v);
      if (!upByRank.has(rank)) upByRank.set(rank, []);
      upByRank.get(rank).push(v);
    }
    for (const v of downOnly) {
      const { rank } = g.node(v);
      for (const a of upByRank.get(rank) ?? []) {
        if (!precedencesByRank.has(rank)) precedencesByRank.set(rank, []);
        precedencesByRank.get(rank).push([a, v]);
      }
    }
  }
  if (!precedencesByRank.size) return;

  const layers = new Map();
  for (const v of g.nodes()) {
    const { rank } = g.node(v);
    if (typeof rank !== 'number') continue;
    if (!layers.has(rank)) layers.set(rank, []);
    layers.get(rank).push(v);
  }

  for (const [rank, precedences] of precedencesByRank) {
    const layer = (layers.get(rank) ?? []).sort((a, b) => g.node(a).order - g.node(b).order);
    stableTopoSort(layer, precedences).forEach((v, i) => { g.node(v).order = i; });
  }
};

// ---------------------------------------------------------------------------
// Point d'entrée
// ---------------------------------------------------------------------------

/**
 * Calcule les positions des noeuds (Dagre, rankdir LR).
 * Seules les arêtes de flux contraignent les rangées : les arêtes de données
 * sont verticales et créeraient des contraintes de rang trompeuses.
 */
export const getLayoutedElements = (nodes, edges) => {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'LR', nodesep: 80, ranksep: 140, edgesep: 40 });

  nodes.forEach((node) => {
    const { w, h } = measured(node);
    g.setNode(node.id, { width: w, height: h });
  });

  const fluxEdges = edges.filter((e) => e.type === 'flux' || !e.type);
  fluxEdges.forEach((e) => g.setEdge(e.source, e.target));

  const constraints = handleOrderConstraints(nodes, fluxEdges);
  const pairs = orderedEdgePairs(fluxEdges);

  dagre.layout(g, {
    // `defaultOrder` est l'ordonnancement natif ; on le rejoue avec les
    // contraintes, puis on corrige ce qu'elles ne savent pas exprimer.
    customOrder: (layoutGraph, defaultOrder) => {
      defaultOrder(layoutGraph, { constraints });
      enforceHandleOrder(layoutGraph, pairs);
    },
  });

  const layoutedNodes = nodes.map((node) => {
    const { x, y } = g.node(node.id);
    const { w, h } = measured(node);
    // Dagre donne le centre ; ReactFlow attend le coin haut-gauche.
    return { ...node, position: { x: x - w / 2, y: y - h / 2 } };
  });

  return { nodes: layoutedNodes, edges };
};
